import { Alert, IconButton, Snackbar, Stack, Slide, type SlideProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ToastContext,
  type ToastController,
  type ToastOptions,
  type ToastVariant,
} from './toastContext';

interface Toast {
  id: number;
  key?: string;
  message: ReactNode;
  variant: ToastVariant;
  duration: number | null;
}

const MAX_VISIBLE = 3;
const DEFAULT_DURATION = 5000;

function SlideTransition(props: SlideProps): JSX.Element {
  return <Slide {...props} direction="left" />;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Max visible toasts (rest queue). Defaults to 3. */
  maxVisible?: number;
}

/**
 * Provider that owns the toast queue. Mount once near the root (typically
 * inside the `ThemeProvider`).
 */
export function ToastProvider({
  children,
  maxVisible = MAX_VISIBLE,
}: ToastProviderProps): JSX.Element {
  const [queue, setQueue] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const enqueue = useCallback(
    (variant: ToastVariant, message: ReactNode, opts?: ToastOptions): number => {
      const id = ++idRef.current;
      setQueue((prev) => {
        if (opts?.key && prev.some((t) => t.key === opts.key)) {
          return prev;
        }
        return [
          ...prev,
          {
            id,
            key: opts?.key,
            message,
            variant,
            duration: opts?.duration === undefined ? DEFAULT_DURATION : opts.duration,
          },
        ];
      });
      return id;
    },
    [],
  );

  const dismiss = useCallback((id: number) => {
    setQueue((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => setQueue([]), []);

  const controller = useMemo<ToastController>(
    () => ({
      success: (message, opts) => enqueue('success', message, opts),
      info: (message, opts) => enqueue('info', message, opts),
      warn: (message, opts) => enqueue('warning', message, opts),
      error: (message, opts) => enqueue('error', message, opts),
      dismiss,
      dismissAll,
    }),
    [enqueue, dismiss, dismissAll],
  );

  const visible = queue.slice(0, maxVisible);

  return (
    <ToastContext.Provider value={controller}>
      {children}
      <Stack
        aria-live="polite"
        spacing={1}
        sx={{
          position: 'fixed',
          top: (theme) => theme.spacing(2),
          right: (theme) => theme.spacing(2),
          zIndex: (theme) => theme.zIndex.snackbar,
          pointerEvents: 'none',
        }}
      >
        {visible.map((t, idx) => (
          <Snackbar
            key={t.id}
            open
            autoHideDuration={t.duration ?? null}
            onClose={(_, reason) => {
              if (reason === 'clickaway') return;
              dismiss(t.id);
            }}
            TransitionComponent={SlideTransition}
            // Inline into the stack — each Snackbar is positioned relative.
            sx={{
              position: 'static',
              transform: 'none',
              pointerEvents: 'auto',
              mt: idx === 0 ? 0 : 1,
            }}
          >
            <Alert
              severity={t.variant}
              variant="filled"
              onClose={() => dismiss(t.id)}
              action={
                <IconButton
                  aria-label="Dismiss notification"
                  size="small"
                  color="inherit"
                  onClick={() => dismiss(t.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              sx={{ minWidth: 280, boxShadow: 3 }}
            >
              {t.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </ToastContext.Provider>
  );
}
